import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonComponent } from '../button/button.component';
import { copyToClipboard } from '../util/clipboard';

/** Raggruppa la chiave a blocchi di quattro caratteri, più facili da ricopiare. */
export function groupSecret(secret: string): string {
  return secret
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

/**
 * Codice QR per configurare l'app di autenticazione, con la chiave in chiaro sotto (per chi la
 * inserisce a mano) e il pulsante per copiarla. Il QR è un'immagine con testo alternativo.
 */
@Component({
  selector: 'bs-qr',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  host: { class: 'flex' },
  template: `
    <div class="flex flex-col items-center gap-3" [attr.data-testid]="testId()">
      <div
        role="img"
        [attr.aria-label]="label()"
        class="size-48 rounded-2xl bg-white p-3 ring-1 ring-inset ring-stone-200 *:size-full dark:ring-stone-700"
        [innerHTML]="safeSvg()"
      ></div>
      @if (secret()) {
        <p class="text-sm text-stone-600 dark:text-stone-300">
          Oppure inserisci questa chiave nell'app:
        </p>
        <p
          class="break-all text-center font-mono text-base text-ink dark:text-stone-50"
          [attr.data-testid]="testId() ? testId() + '-secret' : null"
        >
          {{ grouped() }}
        </p>
        <bs-button
          variant="secondary"
          icon="copy"
          [testId]="testId() ? testId() + '-copy' : null"
          (click)="copy()"
        >
          Copia la chiave
        </bs-button>
        <p class="min-h-5 text-sm text-stone-600 dark:text-stone-300" role="status">
          {{ feedback() }}
        </p>
      }
    </div>
  `,
})
export class QrComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** SVG del QR come restituito dalla nostra API. */
  readonly svg = input.required<string>();
  /** Testo alternativo del QR. */
  readonly label = input("Codice QR per l'app di autenticazione");
  /** Chiave segreta in chiaro, mostrata a gruppi di quattro caratteri. */
  readonly secret = input<string | null>(null);
  readonly testId = input<string | null>(null);

  protected readonly feedback = signal('');
  protected readonly grouped = computed(() => groupSecret(this.secret() ?? ''));

  // Lo SVG arriva solo dalla nostra API (`POST /api/me/totp/setup`), generato dal server a
  // partire dall'URI otpauth: non contiene mai dati inseriti da altri utenti. Il sanitizer di
  // Angular toglierebbe gli elementi SVG, per questo e solo qui si salta il controllo.
  protected readonly safeSvg = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.svg()),
  );

  protected async copy(): Promise<void> {
    const copied = await copyToClipboard((this.secret() ?? '').replace(/\s+/g, ''));
    this.feedback.set(
      copied ? 'Chiave copiata.' : 'Non è stato possibile copiare: ricopiala a mano.',
    );
  }
}
